// lib/modelist/render.ts
// The 3D space's render settings (Fater3DState.render): their defaults, their ranges, and the maths the viewer draws them with.
import type { Fater3DRender } from '#types/types';
import { skyAt, sunlightColour, sunShare } from '#lib/modelist/light';
import { heldInRanges } from '#lib/modelist/ranges';

/** The finish the space opens on. */
export const RENDER_DEFAULTS: Fater3DRender = {
  sunIntensity: 1, environment: 1, exposure: 0,
  toneMapping: 'neutral', shadows: 'standard', shadowSoftness: 0, ground: true, haze: 0, glass: false, reflections: 'sky',
  ao: false, aoIntensity: 1, aoRadius: 0.06, bloom: 0, bloomThreshold: 1, dof: false, dofAperture: 0.3, edges: false, sharp: false,
  contrast: 0, saturation: 0, vignette: 0, grain: 0,
};

export const RENDER_RANGES = {
  sunIntensity: { min: 0, max: 3, step: 0.05 },
  environment: { min: 0, max: 3, step: 0.05 },
  exposure: { min: -3, max: 3, step: 0.1 },
  haze: { min: 0, max: 1, step: 0.01 },
  shadowSoftness: { min: 0, max: 1, step: 0.05 },
  aoIntensity: { min: 0, max: 1, step: 0.05 },
  aoRadius: { min: 0.01, max: 0.2, step: 0.01 },
  bloom: { min: 0, max: 2, step: 0.05 },
  bloomThreshold: { min: 0, max: 2, step: 0.05 },
  dofAperture: { min: 0.05, max: 1, step: 0.05 },
  contrast: { min: -1, max: 1, step: 0.05 },
  saturation: { min: -1, max: 1, step: 0.05 },
  vignette: { min: 0, max: 1, step: 0.05 },
  grain: { min: 0, max: 1, step: 0.05 },
} as const;

/** The side of the sun's shadow map, in texels. */
export const SHADOW_MAP_SIZE = { standard: 2048, high: 4096 } as const;
/** The width the sun's shadow map covers, in models (extent, the model's diagonal). */
export const SHADOW_SPAN = 1.6;
/** The taps of the soft shadow's blur across its radius, each way. */
export const SOFT_SHADOW_SAMPLES = 16;
/**
 * The blur radius of a soft shadow, in texels of its map (three.js's VSM blur, light.shadow.radius): at full softness
 * the edge spreads over 1.5 % of the model each way, on a chair as on a site, whichever map size is chosen.
 */
export const shadowRadius = (softness: number, side: number) => (softness * 0.015 * side) / SHADOW_SPAN;

/** How long the scene rests before its reflections are captured again, so a slider drag captures once. */
export const REFLECTION_REST_MS = 250;

/**
 * Whole looks, each every render setting at once. Default is the space as it opens, its light included
 * (`resetsLight`): the project's default light when it has one, else the opening one, and the sun's place given back
 * to the project. The others are a finish and leave the light where the user put it. None touches the display mode. The one in force is read off the
 * settings, never stored.
 */
export const RENDER_PRESETS: Array<{ label: string; set: Fater3DRender; resetsLight?: true }> = [
  { label: 'Default', set: RENDER_DEFAULTS, resetsLight: true },
  { label: 'Soft', set: { ...RENDER_DEFAULTS, shadows: 'high', shadowSoftness: 0.5, ground: true, ao: true } },
  // A bloom kept to what passes the threshold of 1 (three.js's own example blooms everything at 1.5), a tenth more
  // contrast, a light vignette.
  { label: 'Cinematic', set: { ...RENDER_DEFAULTS, toneMapping: 'aces', shadows: 'high', shadowSoftness: 0.3, ground: true, reflections: 'scene', ao: true, bloom: 0.3, sharp: true, contrast: 0.2, vignette: 0.3 } },
];

/**
 * Under the sky the sun is the light, and no studio fills in: its intensity is multiplied by this, so flat
 * ground under the default sun returns 0.86 of its colour from the sun alone, near what the studio backdrop gives it. Seen on a
 * 900 m site (2026-10-12): at the studio's own intensity a white paving drew grey-blue.
 */
export const DAYLIGHT_SUN = 2.2;
/**
 * What a sky's own light is multiplied by as the model's environment, so that on flat ground it fills a quarter of what
 * that sun gives (a clear day is nearer a fifth, a viewer has no bounce light). Measured on the skies as drawn,
 * cosine-weighted over the upper hemisphere: three.js's Preetham sky sends 0.70 with the default sun, written for an
 * exposure of 0.5. A night stays as dark as it is, and so does the twilight sky that leads to it.
 */
export const SKY_ENVIRONMENT_GAIN = { day: 0.3, night: 1 } as const;
/**
 * The sunlight the surroundings send back, as a white ambient light that follows the sun down: without it a shadow
 * is lit by the blue of the sky alone, and falls navy on a grey ground.
 */
export const SUN_BOUNCE = 0.45;

type Rgb = [number, number, number];
// Colours as the renderer works in them: linear, from the sRGB they are written in (the standard curve, three.js's).
const toLinear = (c: number) => (c < 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const linear = (hex: string): Rgb => [1, 3, 5].map((at) => toLinear(parseInt(hex.slice(at, at + 2), 16) / 255)) as Rgb;
const mix = (a: Rgb, b: Rgb, t: number): Rgb => [0, 1, 2].map((i) => a[i]! + (b[i]! - a[i]!) * t) as Rgb;
const hexOf = (rgb: Rgb) => `#${rgb.map((c) => Math.round(Math.min(1, Math.max(0, c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055)) * 255).toString(16).padStart(2, '0')).join('')}`;

// The colour the haze and Arctic's floor fade into by day; the domes' horizons take over from it.
const DAY_HORIZON = linear('#D5EFFA');
// Once the sun is down: a deep blue overhead, a grey-blue horizon, and the sun's last orange low on its side.
const DUSK = { horizon: linear('#7C88AE'), zenith: linear('#27407A'), glow: linear('#F08A4B') };
const NIGHT = { horizon: linear('#1B2A4A'), zenith: linear('#03060F') };
// What the white of the clouds is multiplied by under a setting sun, and once it is down.
const CLOUDS = { low: linear('#F2B48C'), dusk: linear('#2A3350') };
/**
 * What the dome the model is lit by is multiplied by: the night's gain over the day's. The scene is lit at one gain,
 * the day's, at every hour; this leaves a night as dark as it has always been, and a twilight between the two.
 */
export const LIT_DOME_GAIN = SKY_ENVIRONMENT_GAIN.night / SKY_ENVIRONMENT_GAIN.day;

/**
 * The sky as the viewer draws it at a sun's elevation (light.ts skyAt), colours linear: the day sky until the dome has
 * replaced it; the twilight dome from the moment it shows, how much of the day sky it covers, its colours on their way
 * to the night's and its stars; the clouds until the night, their tint and how much is left of them; and the colour of
 * the horizon, which the haze and Arctic's floor fade into.
 */
export const skyDrawn = (elevation: number) => {
  const { warmth, dusk, night } = skyAt(elevation);
  const horizon = mix(DUSK.horizon, NIGHT.horizon, night);
  return {
    day: dusk < 1,
    dome: dusk > 0 ? { shown: dusk, stars: night, horizon, zenith: mix(DUSK.zenith, NIGHT.zenith, night), glow: mix(DUSK.glow, [0, 0, 0], night) } : null,
    clouds: night < 1 ? { shown: 1 - night, tint: mix(mix([1, 1, 1], CLOUDS.low, warmth * 0.7), CLOUDS.dusk, dusk) } : null,
    fade: hexOf(mix(DAY_HORIZON, horizon, dusk)),
  };
};

/**
 * The sun and what surrounds it as the viewer lights a model with them, colours linear. Under the sky (`sky`, and no
 * drawing) the sun is the light: DAYLIGHT_SUN of it, dimmed and warmed as it gets low, the sky its fill at the
 * day's gain, with the sun's own bounce. Under the studio, and in a drawing (Arctic, Technical) under either, it is the studio's white key, whole
 * until it is under the horizon, beside the studio's own ambient light. `intensity` is a share of the user's own.
 */
export const sunDrawn = ({ sky, drawing, arctic }: { sky: boolean; drawing: boolean; arctic: boolean }, elevation: number) => {
  const lit = sky && !drawing;
  const share = sunShare(lit, elevation);
  return {
    intensity: (arctic ? 1.7 : drawing ? 0.7 : 1.5) * (lit ? DAYLIGHT_SUN : 1) * share,
    colour: (lit ? sunlightColour(skyAt(elevation).warmth).map(toLinear) : [1, 1, 1]) as Rgb,
    casts: share > 0,
    ambient: arctic ? 0.3 : drawing ? 0.9 : lit ? SUN_BOUNCE * share : 0.4,
    gain: lit ? SKY_ENVIRONMENT_GAIN.day : 1,
  };
};

/** A change to the settings with every number held inside its range. */
export const clampRender = (patch: Partial<Fater3DRender>): Partial<Fater3DRender> => heldInRanges<Fater3DRender>(RENDER_RANGES, patch);

/** The unit vector towards the sun in the viewer's axes (glTF: Y up, north -Z, east +X). */
export const sunDirection = (azimuth: number, elevation: number): [number, number, number] => {
  const a = (azimuth * Math.PI) / 180;
  const e = (elevation * Math.PI) / 180;
  return [Math.sin(a) * Math.cos(e), Math.sin(e), -Math.cos(a) * Math.cos(e)];
};

/** The renderer's exposure for a number of stops: each stop doubles the light. */
export const exposureOf = (stops: number) => 2 ** stops;

// At full haze, what stands one model away (extent, the model's diagonal) keeps 85 % of its colour, and what stands
// four models away a tenth: three.js's squared-exponential fog hides 1 - exp(-(density x distance)²).
const HAZE_REACH = 0.4;
/** The fog density for a haze setting, per metre: scaled to the model, so one setting reads alike on a chair and on a site. */
export const hazeDensity = (haze: number, extent: number) => (haze * HAZE_REACH) / Math.max(extent, 1e-6);

/**
 * The occlusion's reach in metres: a share of the model, and for the Render tab's own never past 25 m at the widest
 * setting (1.5 m at the default), since on a 900 m site a share alone dims every facade instead of its corners.
 * Arctic (`drawing`) keeps the uncapped share it has always had; the floor is for a model of a few centimetres.
 */
export const occlusionRadius = (share: number, extent: number, drawing = false) => Math.max(drawing ? extent * share : Math.min(extent * share, share * 25), 0.02);

/** The widest blur of the depth of field, as a share of the picture's height. */
export const BOKEH_MAX_BLUR = 0.01;
/**
 * three.js's BokehPass blurs by (distance from the focus, in metres) x aperture, up to BOKEH_MAX_BLUR: scaled to the
 * model, a full aperture reaches that blur a quarter of the model away from the focus, on a chair as on a site.
 */
export const bokehAperture = (aperture: number, extent: number) => (aperture * 4 * BOKEH_MAX_BLUR) / Math.max(extent, 1e-6);

/** How long the camera rests before the one supersampled redraw, and its samples as a power of two. */
export const SHARP_REST_MS = 200;
export const SHARP_SAMPLE_LEVEL = 3;

/**
 * The passes the settings ask for where the space stands, or null when the canvas draws as it always has, with no
 * composer. Arctic is drawn with occlusion whatever the settings, at the defaults unless the user set their own. A
 * world's splats are not proven to draw inside a composer: beside one, only Arctic's occlusion runs, as before. The
 * depth of field focuses on the orbit's target, which a parallel camera and a walk do not have. A cut is filled by
 * stencil caps (Fater3DSpace's SectionCap), which only the scene's own render honours: the occlusion and the
 * supersampled pass draw the scene again without it and paint the caps over the whole frame, so neither runs in a cut.
 */
export const pipelineOf = (render: Fater3DRender, at: { arctic: boolean; parallel: boolean; world: boolean; walking: boolean; cut: boolean }) => {
  const own = !at.world;
  const ao = at.cut ? null : own && render.ao ? { intensity: render.aoIntensity, radius: render.aoRadius }
    : at.arctic ? { intensity: RENDER_DEFAULTS.aoIntensity, radius: RENDER_DEFAULTS.aoRadius } : null;
  const bloom = own && render.bloom > 0 ? { strength: render.bloom, threshold: render.bloomThreshold } : null;
  const dof = own && render.dof && !at.parallel && !at.walking ? { aperture: render.dofAperture } : null;
  const graded = render.contrast !== 0 || render.saturation !== 0 || render.vignette !== 0 || render.grain !== 0;
  // Contrast is halved and grain kept under an eighth of the range: past that the picture breaks rather than grades.
  const grade = own && graded ? { contrast: 1 + render.contrast * 0.5, saturation: 1 + render.saturation, vignette: render.vignette, grain: render.grain * 0.12 } : null;
  const sharp = own && render.sharp && !at.cut;
  return ao || bloom || dof || grade || sharp ? { ao, bloom, dof, grade, sharp } : null;
};
